import React from 'react';
import { Button, Dropdown } from 'react-bootstrap';
import { CircleCheckBig, CircleX, MoreHorizontal, Pencil, RefreshCw, Trash2 } from 'lucide-react';
import moment from 'moment';

export const getLeaveAllowanceFromStartDate = startDateValue => {
  if (!startDateValue) return 0;

  const startDate = moment(startDateValue);
  if (!startDate.isValid()) return 0;

  const now = moment();
  if (startDate.isAfter(now)) return 0;

  const years = now.diff(startDate, 'years');
  if (years < 1) return 0;
  if (years < 2) return 5;
  if (years < 3) return 10;
  return 15;
};

export const getCompanyEmail = user => {
  if (!user) return '';
  const email = user.email || '';
  if (/^disabled\.[a-f0-9]{24}@released\.local$/i.test(email)) {
    const prev = user.previousEmails;
    return (Array.isArray(prev) && prev.length ? prev[prev.length - 1] : '') || '';
  }
  return email;
};

export const getTenureFromStartDate = startDateValue => {
  if (!startDateValue) return '-';

  const startDate = moment(startDateValue);
  if (!startDate.isValid()) return '-';

  const now = moment();
  if (startDate.isAfter(now)) return '-';

  const years = now.diff(startDate, 'years');
  const months = now
    .clone()
    .subtract(years, 'years')
    .diff(startDate, 'months');

  if (years < 0) return '-';
  if (years === 0 && months === 0) return '< 1 mo';
  if (years === 0) return `${months} mo`;
  if (months === 0) return `${years} yr`;
  return `${years} yr ${months} mo`;
};

export const getEmploymentPeriodLabel = (startDateValue, endDateValue) => {
  if (!startDateValue) return '-';
  const startDate = moment(startDateValue);
  if (!startDate.isValid()) return '-';
  if (endDateValue) {
    const endDate = moment(endDateValue);
    if (endDate.isValid()) {
      return `${startDate.format('MMM D, YYYY')} – ${endDate.format('MMM D, YYYY')}`;
    }
  }
  return getTenureFromStartDate(startDateValue);
};

export const formatHolidayDate = date => (date ? moment(date).format('MMM DD, YYYY') : '—');

export const getTeamManagementOverviewColumns = ({
  setAddEditUser,
  setShowAddEditUserModal,
  setUserToReinvite,
  setStatusChangeMeta
}) => [
  {
    key: 'name',
    label: 'Name',
    sortable: true,
    width: 200,
    render: (value, row) => (
      <div className="d-flex flex-column">
        <span className="font-weight-semibold text-dark">{value}</span>
        <span className="text-muted xsmallFont">{getCompanyEmail(row) || '-'}</span>
      </div>
    )
  },
  {
    key: 'role',
    label: 'Role',
    sortable: true,
    width: 130,
    render: (value, row) => (
      <div
        className={`d-inline-flex rounded-pill ${
          value === 'Admin' ? 'text-white' : 'text-dark border border-secondary'
        }`}
        style={{ backgroundColor: value === 'Admin' && '#EAB308' }}
      >
        <span className="xsmallFont font-weight-semibold px-2">{value || '-'}</span>
      </div>
    )
  },
  {
    key: 'isEnabled',
    label: 'Status',
    sortable: true,
    width: 120,
    render: value => (
      <span className={`text-capitalize font-weight-semibold ${value ? 'text-success' : 'text-danger'}`}>
        {value ? 'Enabled' : 'Disabled'}
      </span>
    )
  },
  {
    key: 'departmentRoles',
    label: 'Department',
    sortable: true,
    width: 150,
    render: value => <span className="text-truncate">{value || '-'}</span>
  },
  {
    key: 'startDate',
    label: 'Employment',
    sortable: true,
    width: 180,
    render: (value, row) => (
      <span className="text-truncate">{getEmploymentPeriodLabel(row.startDate, row.endDate)}</span>
    )
  },
  {
    key: 'daysOffUsed',
    label: 'Time Off',
    sortable: true,
    width: 140,
    render: (value, row) => {
      const allowance = getLeaveAllowanceFromStartDate(row.startDate);
      const used = Number(row.daysOffUsed) || 0;
      if (!allowance && !used) return '-';
      return (
        <span className={`text-truncate ${used >= allowance ? 'text-danger' : ''}`}>
          {used}/{allowance} used
        </span>
      );
    }
  },
  {
    key: 'defaultProcessorFor',
    label: 'Services',
    sortable: true,
    width: 220,
    render: (value, row) => {
      const services = Array.isArray(row.defaultProcessorFor)
        ? row.defaultProcessorFor.filter(Boolean)
        : row.defaultProcessorFor
        ? [row.defaultProcessorFor]
        : [];

      if (services.length === 0) return '-';

      return (
        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
          {services.map(service => (
            <div className={`d-inline-flex rounded-pill border border-secondary`}>
              <span className="xsmallFont font-weight-semibold px-2">{service}</span>
            </div>
          ))}
        </div>
      );
    }
  },
  {
    key: 'actions',
    label: 'Actions',
    sortable: false,
    align: 'right',
    width: 110,
    render: (value, row) => (
      <Dropdown drop="down-start" autoClose="true">
        <Dropdown.Toggle
          as={Button}
          size="sm"
          variant="outline-light"
          className="p-0 d-flex align-items-center justify-content-center border-0 text-dark dropdown-toggle-no-arrow"
          style={{ width: 28, height: 28 }}
        >
          <MoreHorizontal size={16} className="text-muted" />
        </Dropdown.Toggle>
        <Dropdown.Menu align="start" className="p-1">
          <Dropdown.Item
            as={Button}
            size="sm"
            variant=""
            className="py-2 px-2 mr-1 d-flex align-items-center text-dark"
            style={{ borderRadius: '12px' }}
            onClick={e => {
              e.preventDefault();
              setAddEditUser(row);
              setShowAddEditUserModal(true);
            }}
          >
            <Pencil size={14} /> <span className="ml-2">Edit</span>
          </Dropdown.Item>
          {row.isEnabled && (
            <Dropdown.Item
              as={Button}
              size="sm"
              variant=""
              className="py-2 px-2 mr-1 d-flex align-items-center text-dark"
              style={{ borderRadius: '12px' }}
              onClick={e => {
                e.preventDefault();
                setUserToReinvite(row);
              }}
            >
              <RefreshCw size={14} /> <span className="ml-2">Reinvite</span>
            </Dropdown.Item>
          )}
          <Dropdown.Item
            as={Button}
            size="sm"
            variant=""
            className="py-2 px-2 mr-1 d-flex align-items-center text-dark"
            style={{ borderRadius: '12px' }}
            onClick={e => {
              e.preventDefault();
              setStatusChangeMeta({
                user: row,
                isEnabled: !row.isEnabled
              });
            }}
          >
            {row.isEnabled ? <CircleX size={14} /> : <CircleCheckBig size={14} />}
            <span className="ml-2">{row.isEnabled ? 'Disable' : 'Enable'}</span>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    )
  }
];

export const getHolidaysTabColumns = ({
  setEditHoliday,
  setShowAddHolidayModal,
  setToBeDeletedHoliday,
  setShowDeleteAlert
}) => [
  {
    key: 'holidayName',
    label: 'Holiday',
    sortable: true,
    width: 280,
    render: value => <span className="font-weight-bold text-dark">{value}</span>
  },
  {
    key: 'startDate',
    label: 'Start Date',
    sortable: true,
    width: 160,
    render: (value, row) => <span className="font-weight-bold text-muted">{formatHolidayDate(row.startDate)}</span>
  },
  {
    key: 'endDate',
    label: 'End Date',
    sortable: true,
    width: 160,
    render: (value, row) => <span className="font-weight-bold text-muted">{formatHolidayDate(row.endDate)}</span>
  },
  {
    key: 'notes',
    label: 'Notes',
    sortable: true,
    width: 160,
    render: (value, row) => row.notes
  },
  {
    key: 'actions',
    label: 'Actions',
    sortable: false,
    align: 'right',
    width: 80,
    render: (value, row) => (
      <div className="d-flex align-items-center justify-content-start" style={{ gap: 4 }}>
        <Button
          variant="light"
          size="sm"
          className="p-1 border-0 shadow-none bg-transparent hover-muted"
          title="Edit"
          onClick={e => {
            e.stopPropagation();
            setEditHoliday(row);
            setShowAddHolidayModal(true);
          }}
        >
          <Pencil size={16} className="text-muted" />
        </Button>
        <Button
          variant="light"
          size="sm"
          className="p-1 border-0 shadow-none bg-transparent hover-muted"
          title="Delete"
          onClick={e => {
            e.stopPropagation();
            setToBeDeletedHoliday(row._id);
            setShowDeleteAlert(true);
          }}
        >
          <Trash2 size={16} className="text-danger" />
        </Button>
      </div>
    )
  }
];
